
import SunCalc from 'suncalc';
import { calculateGalacticCoreVisibility, type GalacticCorePosition } from './galacticCalculations';
import { formatTimeInTimezone, getTimezoneFromCoordinates } from './timeFormatting';
import { moonDownMinutes, moonTimesForLocalDay } from './moonTimes';
import { getSunTimes } from './sunPosition';

interface LocationData {
  name: string;
  latitude: number;
  longitude: number;
}

export interface DayData {
  date: number;
  fullDate: string;
  sunrise: string;
  sunset: string;
  moonrise: string;
  moonset: string;
  astroTwilightMorning: string;
  astroTwilightEvening: string;
  galacticCoreVisible: boolean;
  galacticCoreStart: string;
  galacticCoreEnd: string;
  moonPhase: number;
  /** SunCalc phase (0/1 = new, 0.5 = full). Optional so older snapshots still typecheck. */
  moonPhaseCycle?: number;
  notVisibleReason?: string;
  maxElevation?: number;
  // Core direction and height through the dark window (ERI-2395). Degrees;
  // riseAzimuth/setAzimuth are the start and end of the window.
  riseAzimuth?: number;
  setAzimuth?: number;
  peakAzimuth?: number;
  startAltitude?: number;
  endAltitude?: number;
  peakAltitude?: number;
  /** Time of the highest point in the dark, in the location's timezone ("1:10 AM"). */
  corePeakTime?: string;
  /**
   * Dark core time (ERI-262): minutes the core is up in full darkness with the
   * moon below the horizon. Only set when the core is visible.
   */
  darkCoreMinutes?: number;
  corePositions?: GalacticCorePosition[];
}

// Shared visibility rating function - used by all calendar views
export function getVisibilityRating(day: DayData): number {
  const moonPercent = Math.round(day.moonPhase * 100);
  
  // If galactic core is not visible, rating is 0
  if (!day.galacticCoreVisible || 
      day.galacticCoreStart === 'Not visible' || 
      day.galacticCoreStart === 'No twilight' ||
      day.galacticCoreStart === 'Below horizon' ||
      day.galacticCoreStart === '--:--' ||
      day.galacticCoreStart === 'Not dark enough' ||
      day.galacticCoreStart === 'Too brief') {
    return 0;
  }
  
  // Rating based on moon phase
  if (moonPercent <= 5) return 10;
  else if (moonPercent <= 10) return 9;
  else if (moonPercent <= 15) return 8;
  else if (moonPercent <= 20) return 7;
  else if (moonPercent <= 25) return 6;
  else if (moonPercent <= 35) return 5;
  else if (moonPercent <= 45) return 4;
  else if (moonPercent <= 55) return 3;
  else if (moonPercent <= 70) return 2;
  else if (moonPercent <= 85) return 1;
  else return 0;
}

// Cache for calculateMonthData results keyed by "lat,lng,month,year"
// Rounded to 4 decimal places (~11m precision) to avoid floating point key mismatches
const monthDataCache = new Map<string, DayData[]>();

export function clearMonthDataCache(): void {
  monthDataCache.clear();
}

export function calculateMonthData(location: LocationData, month: number, year: number): DayData[] {
  // month is now 0-indexed (0 = January, 11 = December)
  const cacheKey = `${location.latitude.toFixed(4)},${location.longitude.toFixed(4)},${month},${year}`;
  const cached = monthDataCache.get(cacheKey);
  if (cached) return cached;

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const data: DayData[] = [];

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  for (let day = 1; day <= daysInMonth; day++) {
    // Use UTC date at noon to ensure calculations are correct for all timezones
    // This prevents timezone offset issues when users in one timezone view locations in another
    const date = new Date(Date.UTC(year, month, day, 12, 0, 0));
    const fullDate = `${monthNames[month]} ${day}`;
    const dayData = calculateDayData(location, date, day, fullDate);
    data.push(dayData);
  }

  monthDataCache.set(cacheKey, data);
  return data;
}

export function calculateDayData(location: LocationData, date: Date, dayNumber: number, fullDate: string): DayData {
  const { latitude, longitude } = location;
  
  // Get the timezone for this location
  const locationTimezone = getTimezoneFromCoordinates(latitude, longitude);

  // Use SunCalc for accurate astronomical calculations
  const sunTimes = getSunTimes(date, latitude, longitude);
  // The location's own day, not the browser's (ERI-2380). `date` is noon UTC on the calendar day.
  const moonTimes = moonTimesForLocalDay(
    date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), latitude, longitude, locationTimezone,
  );
  const moonIllumination = SunCalc.getMoonIllumination(date);
  
  // Calculate galactic core visibility during astronomical darkness only
  const galacticCore = calculateGalacticCoreVisibility(
    date, 
    latitude, 
    longitude, 
    sunTimes
  );

  // Format display values based on visibility - all in location timezone
  let coreStartDisplay = '--:--';
  let coreEndDisplay = '--:--';
  let notVisibleReason = undefined;

  if (galacticCore.visible && galacticCore.start && galacticCore.end) {
    coreStartDisplay = formatTimeInTimezone(galacticCore.start, locationTimezone);
    coreEndDisplay = formatTimeInTimezone(galacticCore.end, locationTimezone);
  } else if (galacticCore.reason === 'below_horizon') {
    coreStartDisplay = 'Below horizon';
    coreEndDisplay = 'Below horizon';
    notVisibleReason = 'The galactic core does not rise above the horizon at this latitude.';
  } else if (galacticCore.reason === 'no_twilight') {
    coreStartDisplay = 'No twilight';
    coreEndDisplay = 'No twilight';
    notVisibleReason = 'No astronomical twilight available for this date.';
  } else if (galacticCore.reason === 'no_dark_overlap') {
    coreStartDisplay = 'Not dark enough';
    coreEndDisplay = 'Not dark enough';
    notVisibleReason = 'The galactic core is not visible during astronomical darkness.';
  } else if (galacticCore.reason === 'insufficient_duration') {
    coreStartDisplay = 'Too brief';
    coreEndDisplay = 'Too brief';
    notVisibleReason = `Visibility window too short (${galacticCore.durationMinutes || 0} minutes). Minimum ${15} minutes needed for practical astrophotography.`;
  }

  return {
    date: dayNumber,
    fullDate: fullDate,
    sunrise: formatTimeInTimezone(sunTimes.sunrise, locationTimezone),
    sunset: formatTimeInTimezone(sunTimes.sunset, locationTimezone),
    moonrise: formatTimeInTimezone(moonTimes.rise, locationTimezone),
    moonset: formatTimeInTimezone(moonTimes.set, locationTimezone),
    astroTwilightMorning: formatTimeInTimezone(sunTimes.nightEnd, locationTimezone),
    astroTwilightEvening: formatTimeInTimezone(sunTimes.night, locationTimezone),
    galacticCoreVisible: galacticCore.visible,
    galacticCoreStart: coreStartDisplay,
    galacticCoreEnd: coreEndDisplay,
    moonPhase: moonIllumination.fraction,
    moonPhaseCycle: moonIllumination.phase,
    notVisibleReason: notVisibleReason,
    maxElevation: galacticCore.maxElevation || 0,
    riseAzimuth: galacticCore.riseAzimuth,
    setAzimuth: galacticCore.setAzimuth,
    peakAzimuth: galacticCore.peakAzimuth,
    startAltitude: galacticCore.startAltitude,
    endAltitude: galacticCore.endAltitude,
    peakAltitude: galacticCore.peakAltitude,
    corePeakTime: galacticCore.visible && galacticCore.peakTime
      ? formatTimeInTimezone(galacticCore.peakTime, locationTimezone)
      : undefined,
    darkCoreMinutes: galacticCore.visible && galacticCore.start && galacticCore.end
      ? moonDownMinutes(galacticCore.start, galacticCore.end, latitude, longitude)
      : undefined,
    corePositions: galacticCore.positions,
  };
}
